/**
 * Sync Manager
 *
 * Background sync manager that handles syncing offline data to the server.
 * Features:
 * - Automatic sync when online
 * - Exponential backoff for retries
 * - Batch processing
 * - Status tracking
 */

import {
  getUnsyncedEntries,
  updateOfflineEntry,
  deleteOfflineEntry,
  getPendingSessionUpdates,
  markSessionUpdateSynced,
  incrementSessionUpdateAttempts,
  deleteSessionUpdate
} from './offlineDB';
import { submitDataEntry, updateDataEntryOnServer, submitSessionUpdate } from 'api/dataCollection';

// Sync configuration.
//
// RETRY PACING IS THE FIXED INTERVAL, not per-entry backoff. A failed entry simply
// waits for the next sweep and is retried then, up to maxRetries. This once carried
// retryDelayMs / maxRetryDelayMs / backoffMultiplier for an exponential backoff helper
// that was written but never called, so the three settings described behaviour the sync
// engine has never had — removed with it rather than left to mislead the next reader.
const SYNC_CONFIG = {
  maxRetries: 5, // Maximum retry attempts per entry
  batchSize: 10, // Process 10 entries at a time
  syncIntervalMs: 30000, // Check for pending entries every 30 seconds
  deleteOnSuccess: false // Keep entries in IndexedDB as 'synced' — cleaned up on next login
};

/* THE SERVER'S REFUSAL CODES FOR AN EDIT TO ALREADY-RECORDED DATA.
 *
 * Mirrors app/services/data_collection_amend.py. A therapist COLLECTS; once the session
 * is closed only a supervisor may correct what was collected, and the server enforces
 * that on every path that writes an existing row (PUT, DELETE, and the offline upsert).
 *
 * These are kept apart from the read-only-sheet 403 on purpose: that one legitimately
 * throws the local entry away, and doing the same here would silently destroy an edit
 * the therapist still needs to hand to their supervisor.
 */
const AMENDMENT_REFUSAL_CODES = new Set([
  'data_collection_session_closed',
  'data_collection_not_collector',
  'data_collection_already_amended'
]);

// Sync status enum
export const SYNC_STATUS = {
  PENDING: 'pending',
  SYNCING: 'syncing',
  SYNCED: 'synced',
  FAILED: 'failed',
  DRAFT: 'draft' // Entry being actively edited — background sync must skip
};

// Internal state
let syncIntervalId = null;
let isSyncing = false;
let isOnline = true;
let syncListeners = [];

/**
 * Notify all listeners of sync status changes
 */
const notifyListeners = (event) => {
  syncListeners.forEach((listener) => {
    try {
      listener(event);
    } catch (error) {
      console.error('[SyncManager] Listener error:', error);
    }
  });
};

/**
 * Subscribe to sync events
 * @param {Function} listener - Callback function (event) => void
 * @returns {Function} Unsubscribe function
 */
export const subscribeToSync = (listener) => {
  syncListeners.push(listener);
  return () => {
    syncListeners = syncListeners.filter((l) => l !== listener);
  };
};

/**
 * Sync a single data entry
 */
const syncEntry = async (entry) => {
  const entryId = entry.id;

  // NOTE: we deliberately do NOT drop entries proactively from the client's cached
  // permissions — that cache can be stale and would DESTROY valid data before the server is
  // ever asked. The server is the sole authority: a genuinely read-only sheet is excluded in
  // the 403 branch below; everything else is written normally.

  // Double-check we're online before syncing
  if (!isOnline) {
    return { success: false, entryId, error: 'Offline' };
  }

  try {
    // Update status to syncing
    await updateOfflineEntry(entryId, {
      syncStatus: SYNC_STATUS.SYNCING,
      lastSyncAttempt: new Date().toISOString()
    });

    notifyListeners({ type: 'entry-syncing', entryId, formType: entry.formType });

    // If entry was already synced (has serverId), update instead of creating new
    const metadata = {
      sessionId: entry.sessionId,
      childId: entry.childId,
      childName: entry.childName,
      therapistId: entry.therapistId,
      therapistName: entry.therapistName,
      sessionDate: entry.sessionDate,
      offlineId: entry.offlineId || String(entry.id),
      isOfflineEntry: true
    };

    let response;
    if (entry.serverId) {
      response = await updateDataEntryOnServer(entry.serverId, entry.formType, entry.data, metadata);
    } else {
      response = await submitDataEntry(entry.formType, entry.data, metadata);
    }

    // Success - mark as synced or delete
    if (SYNC_CONFIG.deleteOnSuccess) {
      await deleteOfflineEntry(entryId);
    } else {
      await updateOfflineEntry(entryId, {
        syncStatus: SYNC_STATUS.SYNCED,
        synced: true,
        syncedAt: new Date().toISOString(),
        serverId: response.id,
        serverTimestamp: response.serverTimestamp
      });
    }

    notifyListeners({
      type: 'entry-synced',
      entryId,
      formType: entry.formType,
      serverId: response.id
    });

    return { success: true, entryId };
  } catch (error) {
    const statusCode = error?.status ?? error?.response?.status;
    // The server names WHY it refused, in the detail body. Two 403s reach here and they
    // must be handled differently — one is a sheet nobody may write, the other is a
    // record this person may no longer change.
    const refusalCode = error?.detail?.code ?? error?.response?.data?.detail?.code ?? null;

    // 403, THE AMENDMENT RULE. This entry already exists on the server and the therapist
    // may no longer change it: the session is closed, or a supervisor has since corrected
    // it. The SERVER COPY IS THE GOOD ONE, so do NOT discard the local entry the way the
    // read-only branch does — that would destroy the therapist's own record of what they
    // meant to change, with nothing to show for it. Park it as FAILED, with the server's
    // message, so it is visible on the sync page and can be taken to a supervisor.
    if (statusCode === 403 && AMENDMENT_REFUSAL_CODES.has(refusalCode)) {
      const message = error?.detail?.message || 'Recorded data can only be corrected by a supervisor.';
      // syncAttempts is pushed to the retry ceiling on purpose. This refusal is a RULE,
      // not a transient failure — retrying it every 30 seconds would be refused
      // identically forever while keeping the entry in the pending queue.
      // getUnsyncedEntries only re-picks a FAILED entry while attempts < maxRetries, so
      // this parks it where the therapist can see it and act on it.
      await updateOfflineEntry(entryId, {
        syncStatus: SYNC_STATUS.FAILED,
        syncAttempts: SYNC_CONFIG.maxRetries,
        syncError: message,
        lastSyncAttempt: new Date().toISOString()
      });
      notifyListeners({ type: 'entry-amendment-refused', entryId, formType: entry.formType, error: message });
      return { success: false, entryId, error: message, amendmentRefused: true };
    }

    // 403 = the server refuses this write because the user holds this data sheet at
    // Full-Read. EXCLUDE it from the write: drop the entry and report success so the rest
    // of the session syncs and the submit never fails. This also covers the case where the
    // client's cached permissions were stale (so the proactive skip above didn't catch it)
    // — the server is the source of truth. Read-only fields simply aren't written.
    if (statusCode === 403) {
      await deleteOfflineEntry(entryId);
      notifyListeners({ type: 'entry-skipped-readonly', entryId, formType: entry.formType });
      return { success: true, entryId, skipped: true };
    }

    console.error(`[SyncManager] Failed to sync entry ${entryId}:`, error);

    const newAttempts = (entry.syncAttempts || 0) + 1;
    const shouldRetry = newAttempts < SYNC_CONFIG.maxRetries;

    await updateOfflineEntry(entryId, {
      syncStatus: shouldRetry ? SYNC_STATUS.PENDING : SYNC_STATUS.FAILED,
      syncAttempts: newAttempts,
      syncError: error.message,
      lastSyncAttempt: new Date().toISOString()
    });

    notifyListeners({
      type: 'entry-failed',
      entryId,
      formType: entry.formType,
      error: error.message,
      attempts: newAttempts,
      willRetry: shouldRetry
    });

    return { success: false, entryId, error: error.message };
  }
};

/**
 * Sync a single session update
 */
const syncSessionUpdate = async (update) => {
  const updateId = update.id;

  try {
    notifyListeners({ type: 'session-syncing', updateId, sessionId: update.sessionId });

    // Submit to API
    await submitSessionUpdate(update.sessionId, update.data);

    // Success - delete from IndexedDB (server is source of truth)
    if (SYNC_CONFIG.deleteOnSuccess) {
      await deleteSessionUpdate(updateId);
    } else {
      await markSessionUpdateSynced(updateId);
    }

    notifyListeners({
      type: 'session-synced',
      updateId,
      sessionId: update.sessionId
    });

    return { success: true, updateId };
  } catch (error) {
    console.error(`[SyncManager] Failed to sync session update ${updateId}:`, error);

    const newAttempts = (update.syncAttempts || 0) + 1;
    await incrementSessionUpdateAttempts(updateId);

    notifyListeners({
      type: 'session-failed',
      updateId,
      sessionId: update.sessionId,
      error: error.message,
      attempts: newAttempts
    });

    return { success: false, updateId, error: error.message };
  }
};

/**
 * Process sync queue - main sync function
 */
export const processSyncQueue = async () => {
  if (isSyncing) {
    return { skipped: true };
  }

  if (!isOnline) {
    return { offline: true };
  }

  // Skip sync if user is not authenticated — avoids unnecessary 401 API calls
  if (!localStorage.getItem('serviceToken')) {
    return { noAuth: true };
  }

  isSyncing = true;
  notifyListeners({ type: 'sync-started' });

  const results = {
    // `skipped` = entries the server refused with 403 because the sheet is Full-Read; they
    // are excluded from the write, not counted as synced (would falsely claim data saved) nor
    // failed (would be a scary error for an intentional read-only sheet).
    entries: { synced: 0, failed: 0, skipped: 0 },
    sessions: { synced: 0, failed: 0 },
    total: { synced: 0, failed: 0, skipped: 0 }
  };

  try {
    // 1. Sync session updates first (higher priority)
    const pendingSessionUpdates = await getPendingSessionUpdates();
    for (const update of pendingSessionUpdates) {
      const result = await syncSessionUpdate(update);
      if (result.success) {
        results.sessions.synced++;
      } else {
        results.sessions.failed++;
      }
    }

    // 2. Sync data entries
    const unsyncedEntries = await getUnsyncedEntries();

    // Filter to only pending entries (not already syncing or failed with max retries)
    const entriesToSync = unsyncedEntries.filter((entry) => {
      const status = entry.syncStatus || SYNC_STATUS.PENDING;
      const attempts = entry.syncAttempts || 0;

      // Sync if pending, or failed but under max retries
      return status === SYNC_STATUS.PENDING || (status === SYNC_STATUS.FAILED && attempts < SYNC_CONFIG.maxRetries);
    });

    // Process in batches
    for (let i = 0; i < entriesToSync.length; i += SYNC_CONFIG.batchSize) {
      const batch = entriesToSync.slice(i, i + SYNC_CONFIG.batchSize);

      for (const entry of batch) {
        const result = await syncEntry(entry);
        if (result.skipped) {
          results.entries.skipped++;
        } else if (result.success) {
          results.entries.synced++;
        } else {
          results.entries.failed++;
        }
      }

      // Small delay between batches
      if (i + SYNC_CONFIG.batchSize < entriesToSync.length) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    results.total.synced = results.entries.synced + results.sessions.synced;
    results.total.failed = results.entries.failed + results.sessions.failed;
    results.total.skipped = results.entries.skipped;

    notifyListeners({
      type: 'sync-completed',
      results
    });

    return results;
  } catch (error) {
    console.error('[SyncManager] Sync error:', error);
    notifyListeners({ type: 'sync-error', error: error.message });
    throw error;
  } finally {
    isSyncing = false;
  }
};

/**
 * Trigger an immediate sync
 */
export const triggerSync = async () => {
  return processSyncQueue();
};

/**
 * Reset any entries stuck in 'syncing' state back to 'pending'
 * This handles cases where the app closed or network dropped mid-sync
 */
export const recoverStuckEntries = async () => {
  try {
    const entries = await getUnsyncedEntries();
    const stuckEntries = entries.filter((e) => e.syncStatus === SYNC_STATUS.SYNCING);

    if (stuckEntries.length > 0) {
      for (const entry of stuckEntries) {
        await updateOfflineEntry(entry.id, {
          syncStatus: SYNC_STATUS.PENDING,
          syncError: 'Sync interrupted - will retry'
        });
      }

      notifyListeners({ type: 'entries-recovered', count: stuckEntries.length });
    }
  } catch (error) {
    console.error('[SyncManager] Failed to recover stuck entries:', error);
  }
};

/**
 * Promote any DRAFT entries to PENDING.
 * Called on app startup (startSyncManager) to recover entries from sessions
 * that were abandoned without clicking "End Session".
 * Safe to call on startup because there is no active data collection session yet.
 */
export const promoteDraftEntries = async () => {
  try {
    const entries = await getUnsyncedEntries();
    const draftEntries = entries.filter((e) => e.syncStatus === SYNC_STATUS.DRAFT);

    if (draftEntries.length > 0) {
      for (const entry of draftEntries) {
        await updateOfflineEntry(entry.id, { syncStatus: SYNC_STATUS.PENDING });
      }

      notifyListeners({ type: 'drafts-promoted', count: draftEntries.length });
    }
  } catch (error) {
    console.error('[SyncManager] Failed to promote draft entries:', error);
  }
};

/**
 * Start the background sync manager
 */
export const startSyncManager = async () => {
  if (syncIntervalId) {
    return;
  }

  // First, recover any entries stuck in 'syncing' state from previous session
  await recoverStuckEntries();

  // Promote any DRAFT entries from abandoned sessions (user navigated away
  // without clicking "End Session" or app was closed mid-session)
  await promoteDraftEntries();

  // Run initial sync
  processSyncQueue().catch((error) => {
    console.error('[SyncManager] Initial sync failed:', error);
  });

  // Set up periodic sync
  syncIntervalId = setInterval(() => {
    processSyncQueue().catch((error) => {
      console.error('[SyncManager] Periodic sync failed:', error);
    });
  }, SYNC_CONFIG.syncIntervalMs);

  notifyListeners({ type: 'manager-started' });
};

/**
 * Stop the background sync manager
 */
export const stopSyncManager = () => {
  if (syncIntervalId) {
    clearInterval(syncIntervalId);
    syncIntervalId = null;
    notifyListeners({ type: 'manager-stopped' });
  }
};

/**
 * Update online status (call when network status changes)
 */
export const setOnlineStatus = async (online) => {
  const wasOffline = !isOnline && online;
  isOnline = online;

  if (wasOffline) {
    // Promote any DRAFT entries to PENDING so they get picked up
    // (handles entries from sessions ended while offline)
    await promoteDraftEntries();
    // Trigger sync when coming back online
    triggerSync().catch((error) => {
      console.error('[SyncManager] Post-reconnect sync failed:', error);
    });
  }
};

/**
 * Get current sync status
 */
export const getSyncStatus = () => ({
  isRunning: syncIntervalId !== null,
  isSyncing,
  isOnline
});

/**
 * Get sync queue summary
 */
export const getSyncQueueSummary = async () => {
  const entries = await getUnsyncedEntries();
  const sessionUpdates = await getPendingSessionUpdates();

  const summary = {
    entries: {
      pending: entries.filter((e) => !e.syncStatus || e.syncStatus === SYNC_STATUS.PENDING).length,
      syncing: entries.filter((e) => e.syncStatus === SYNC_STATUS.SYNCING).length,
      failed: entries.filter((e) => e.syncStatus === SYNC_STATUS.FAILED).length,
      total: entries.length
    },
    sessions: {
      pending: sessionUpdates.length,
      total: sessionUpdates.length
    },
    total: entries.length + sessionUpdates.length
  };

  return summary;
};

/**
 * Retry failed entries
 */
export const retryFailedEntries = async () => {
  const entries = await getUnsyncedEntries();
  const failedEntries = entries.filter((e) => e.syncStatus === SYNC_STATUS.FAILED);

  for (const entry of failedEntries) {
    await updateOfflineEntry(entry.id, {
      syncStatus: SYNC_STATUS.PENDING,
      syncAttempts: 0,
      syncError: null
    });
  }

  // Trigger sync
  return triggerSync();
};

/**
 * Sync only selected entries by ID
 * @param {Array<number|string>} entryIds - Array of entry IDs to sync
 * @returns {Promise<Object>} Sync results
 */
export const syncSelectedEntries = async (entryIds) => {
  if (isSyncing) {
    return { skipped: true };
  }

  if (!isOnline) {
    return { offline: true };
  }

  if (!entryIds || entryIds.length === 0) {
    return { noSelection: true };
  }

  isSyncing = true;
  notifyListeners({ type: 'sync-started', selectedCount: entryIds.length });

  const results = {
    entries: { synced: 0, failed: 0 },
    sessions: { synced: 0, failed: 0 },
    total: { synced: 0, failed: 0 }
  };

  try {
    // Separate session updates from data entries
    const sessionIds = entryIds
      .filter((id) => typeof id === 'string' && id.startsWith('session-'))
      .map((id) => parseInt(id.replace('session-', ''), 10));

    const dataEntryIds = entryIds.filter((id) => typeof id === 'number' || (typeof id === 'string' && !id.startsWith('session-')));

    // 1. Sync selected session updates
    if (sessionIds.length > 0) {
      const pendingSessionUpdates = await getPendingSessionUpdates();
      const selectedSessionUpdates = pendingSessionUpdates.filter((u) => sessionIds.includes(u.id));

      for (const update of selectedSessionUpdates) {
        const result = await syncSessionUpdate(update);
        if (result.success) {
          results.sessions.synced++;
        } else {
          results.sessions.failed++;
        }
      }
    }

    // 2. Sync selected data entries
    if (dataEntryIds.length > 0) {
      const unsyncedEntries = await getUnsyncedEntries();
      const selectedEntries = unsyncedEntries.filter((e) => dataEntryIds.includes(e.id));

      for (const entry of selectedEntries) {
        const result = await syncEntry(entry);
        if (result.success) {
          results.entries.synced++;
        } else {
          results.entries.failed++;
        }
      }
    }

    results.total.synced = results.entries.synced + results.sessions.synced;
    results.total.failed = results.entries.failed + results.sessions.failed;

    notifyListeners({
      type: 'sync-completed',
      results,
      isSelectedSync: true
    });

    return results;
  } catch (error) {
    console.error('[SyncManager] Selected sync error:', error);
    notifyListeners({ type: 'sync-error', error: error.message });
    throw error;
  } finally {
    isSyncing = false;
  }
};

export default {
  SYNC_STATUS,
  startSyncManager,
  stopSyncManager,
  triggerSync,
  processSyncQueue,
  setOnlineStatus,
  getSyncStatus,
  getSyncQueueSummary,
  retryFailedEntries,
  syncSelectedEntries,
  recoverStuckEntries,
  promoteDraftEntries,
  subscribeToSync
};
